import { useTranslation } from '@packlink/translation-provider';
import {
    getButtonStyles,
    getContainerStyles,
    getEditIconStyles,
    getTitleWrapperStyles,
    getSwitchStyles,
    getTagStyles,
    getTitleStyles,
} from './EmailTemplateListItemStyles';
import { useToggle } from '@packlink/utils';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    IconSize,
    Switch,
    Tag,
    TagColor,
    TagVariant,
    Typography,
} from '@shipengine/giger';
import { EmailsSettings } from '@packlink/packlink-sdk';
import { emailTypesKeys } from '../../constants';
import { ToggleDialog } from '../../../ToggleDialog/ToggleDialog';
import { IconNames } from '@shipengine/giger-theme';
import { useToggleActiveTemplate } from '@common/hooks/useToggleActiveTemplate';

export interface EmailTemplateListItemProps {
    template: EmailsSettings;
    onEdit: (template: EmailsSettings) => void;
    onToggle?: (active: boolean) => void;
}

export function EmailTemplateListItem({ template, onEdit, onToggle }: EmailTemplateListItemProps): JSX.Element {
    const { t } = useTranslation();
    const { state: activeToggle, toggle } = useToggle(template.active);
    const { state: isDialogOpen, toggle: toggleDialog } = useToggle();
    const { toggleActiveTemplate } = useToggleActiveTemplate();

    const handleEdit = () => {
        onEdit(template);
    };

    const onChangeSwitch = () => {
        toggleDialog();
    };

    const disableActiveTemplate = () => {
        toggleDialog();

        toggleActiveTemplate({
            emailType: template.emailType,
            active: !template.active,
            onToggle: toggle,
            onSuccess: () => onToggle?.(!template.active),
        });
    };

    return (
        <div css={getContainerStyles}>
            <section>
                <div css={getTitleWrapperStyles}>
                    <Switch
                        name={`is-enabled-${template.emailType}`}
                        value={activeToggle}
                        isChecked={activeToggle}
                        onChange={onChangeSwitch}
                        css={getSwitchStyles}
                    >
                        <p hidden>{`${t('branded-emails.template-list.active-email')} ${activeToggle}`}</p>
                    </Switch>

                    <div css={getTitleStyles}>
                        <Typography variant="heading5" bold>
                            {t(`branded-emails.template-list.${emailTypesKeys[template.emailType]}-title`)}
                        </Typography>

                        <Tag
                            variant={TagVariant.LINEAL_FILLED}
                            color={template.active ? TagColor.SUCCESS : TagColor.GRAY}
                            uppercase={false}
                            css={getTagStyles}
                        >
                            {t(
                                template.active
                                    ? 'branded-emails.template-list.active-email'
                                    : `branded-emails.template-list.inactive-email`,
                            )}
                        </Tag>
                    </div>
                </div>
                <Typography variant="body2">
                    {t(`branded-emails.template-list.${emailTypesKeys[template.emailType]}-description`)}
                </Typography>
            </section>

            <section>
                <Button
                    variant={ButtonVariant.OUTLINED}
                    onClick={handleEdit}
                    css={getButtonStyles}
                    size={ButtonSize.SMALL}
                >
                    <Icon name={IconNames.EDIT} size={IconSize.SIZE_SMALL} css={getEditIconStyles} />
                    {t('branded-emails.template-list.cta-edit')}
                </Button>
            </section>

            <ToggleDialog
                activeToggle={activeToggle}
                isOpen={isDialogOpen}
                onClose={toggleDialog}
                onSave={disableActiveTemplate}
                templateKey={t(`branded-emails.template-list.${emailTypesKeys[template.emailType]}-title`)}
            />
        </div>
    );
}
